// Chamadas do convite "Meta de Frentista com IA" (feature 161). Sempre httpClientReactAdmin
// com `unwrapData` (as da rede passam por `call`; `sendOffer`, interna da PontuaX, não é escopada pela rede do token);
// nunca useGetOne (o convite não é um resource do react-admin).
import { DEFAULT_API_ADMIN } from '../constants';
import { getChainId, httpClientReactAdmin } from '../lib';
import {
  GoalLevel, InsightKind, InsightResponse, OfferApiError, OfferErrorCode, OfferView, SendOfferError, SendOfferResult,
  toOfferApiError, toSendOfferError, unwrapData,
} from '../models/attendantGoalOffer';

type Locale = 'pt-br' | 'es';

const offerBase = (): string => {
  const chainId = getChainId();
  if (!chainId) throw new OfferApiError('chain_missing', 0, null, null);
  return `${DEFAULT_API_ADMIN}/chains/${chainId}/attendant-goal-offer`;
};

// `path` é o sufixo depois de /attendant-goal-offer. A base é calculada aqui (função async, fora do try)
// para `chain_missing` virar sempre rejeição de Promise, nunca throw síncrono no render do consumidor.
const call = async <T>(path: string, method: 'GET' | 'POST', body?: unknown): Promise<T | null> => {
  const url = `${offerBase()}${path}`;
  try {
    const { json } = await httpClientReactAdmin(
      url,
      method === 'GET' ? { method } : { method, body: JSON.stringify(body ?? {}) },
    );
    return unwrapData<T>(json);
  } catch (err) {
    throw toOfferApiError(err);
  }
};

/** Convite visível da rede do token; `null` = sem convite (o card some sem aviso). */
export const getOffer = (): Promise<OfferView | null> => call<OfferView>('', 'GET');

export const getOfferInsight = (offerId: string, kind: InsightKind, locale: Locale): Promise<InsightResponse | null> =>
  call<InsightResponse>(`/${offerId}/ai-insight?kind=${kind}&locale=${locale}`, 'GET');

/** "Agora não": devolve o convite (ou `null`, que esconde o card). */
export const dismissOffer = (offerId: string): Promise<OfferView | null> =>
  call<OfferView>(`/${offerId}/dismiss`, 'POST');

/**
 * Ativa a degustação. No 409 `stale_suggestion` o backend manda o convite novo no erro; se não mandar,
 * busca no GET para a tela sempre receber a sugestão atualizada em `error.offer`.
 */
export const activateOffer = async (
  offerId: string,
  suggestionVersion: number,
  levels: Record<string, GoalLevel>,
): Promise<OfferView> => {
  try {
    const offer = await call<OfferView>(`/${offerId}/activate`, 'POST', { suggestionVersion, levels });
    if (!offer) throw new OfferApiError('empty_response', 0, null, null);
    return offer;
  } catch (err) {
    const e = err as OfferApiError;
    if (e.code === OfferErrorCode.staleSuggestion && !e.offer) {
      const offer = await getOffer().catch(() => null);
      throw new OfferApiError(e.message, e.status, e.code, offer);
    }
    throw e;
  }
};

export const requestContinue = async (offerId: string): Promise<OfferView> => {
  const offer = await call<OfferView>(`/${offerId}/continue`, 'POST');
  if (!offer) throw new OfferApiError('empty_response', 0, null, null);
  return offer;
};

export const markOfferSeen = async (offerId: string): Promise<void> => {
  await call<{ ok: true }>(`/${offerId}/seen`, 'POST');
};

/**
 * Interno PontuaX (isAdmin + attendant-goal-internal): analisa a rede e cria (ou recalcula) o convite. A análise é síncrona,
 * de 5 a 30 s em rede grande. Recusa e falha viram `SendOfferError`; o backend nunca responde 403 aqui, e o 401
 * (token vencido) o `httpClientReactAdmin` já renova uma vez.
 */
export const sendOffer = async (chainId: string): Promise<SendOfferResult> => {
  try {
    const { json } = await httpClientReactAdmin(`${DEFAULT_API_ADMIN}/attendant-goal-offers/send`, {
      method: 'POST',
      body: JSON.stringify({ chainId }),
    });
    const result = unwrapData<SendOfferResult>(json);
    if (!result) throw new SendOfferError('empty_response', 0, null, null);
    return result;
  } catch (err) {
    throw err instanceof SendOfferError ? err : toSendOfferError(err);
  }
};
